import React, { useMemo, useState } from "react";

const moleculeViewerCss = `
.biomol-viewer {
  width: 100%;
  min-height: 560px;
  display: grid;
  grid-template-rows: auto minmax(360px, 1fr) auto;
  gap: 14px;
  color: #07111f;
}

.biomol-viewer * {
  box-sizing: border-box;
}

.biomol-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  padding: 18px;
  border-radius: 20px;
  border: 1px solid #cddcec;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.09), transparent 32%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 12px 30px rgba(7, 17, 31, 0.055);
}

.biomol-head p {
  margin: 0 0 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.biomol-head h3 {
  margin: 0;
  color: #07111f;
  font-size: 24px;
  line-height: 1.08;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.biomol-head span {
  display: block;
  margin-top: 7px;
  color: #66778e;
  font-size: 13px;
  line-height: 1.45;
}

.biomol-badge-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  justify-content: flex-end;
}

.biomol-badge-stack em {
  padding: 6px 10px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-style: normal;
  font-weight: 900;
  white-space: nowrap;
}

.biomol-stage {
  position: relative;
  min-height: 420px;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid #d9e8f6;
  background:
    radial-gradient(circle at 50% 45%, rgba(24, 183, 255, 0.16), transparent 34%),
    radial-gradient(circle at 18% 18%, rgba(31, 123, 193, 0.14), transparent 28%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.04),
    0 24px 60px rgba(3, 6, 12, 0.15);
}

.biomol-stage::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(24,183,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(24,183,255,0.05) 1px, transparent 1px);
  background-size: 34px 34px;
  pointer-events: none;
}

.biomol-orbit {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(62vw, 540px);
  height: min(62vw, 540px);
  max-width: 540px;
  max-height: 540px;
  min-width: 280px;
  min-height: 280px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  border: 1px solid rgba(24, 183, 255, 0.16);
  animation: biomolOrbitPulse 3.8s ease-in-out infinite;
}

.biomol-orbit.o2 {
  width: min(48vw, 410px);
  height: min(48vw, 410px);
  max-width: 410px;
  max-height: 410px;
  min-width: 210px;
  min-height: 210px;
  animation-delay: 0.3s;
}

.biomol-orbit.o3 {
  width: min(34vw, 290px);
  height: min(34vw, 290px);
  max-width: 290px;
  max-height: 290px;
  min-width: 150px;
  min-height: 150px;
  animation-delay: 0.6s;
}

.biomol-svg {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  min-height: 420px;
}

.biomol-node {
  cursor: pointer;
  user-select: none;
}

.biomol-node circle {
  transition: 0.16s ease;
}

.biomol-node:hover circle {
  stroke-width: 5;
}

.biomol-node.selected circle {
  stroke: #fbbf24;
  stroke-width: 5;
}

.biomol-bond {
  filter: drop-shadow(0 0 6px rgba(24,183,255,0.18));
}

.biomol-empty {
  position: absolute;
  inset: 18px;
  z-index: 3;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 24px;
  border-radius: 20px;
  border: 1px dashed rgba(24, 183, 255, 0.30);
  color: #c8f1ff;
  background: rgba(255,255,255,0.04);
}

.biomol-empty strong {
  display: block;
  margin-bottom: 8px;
  color: white;
  font-size: 22px;
  font-weight: 950;
}

.biomol-empty span {
  color: #a9bdd3;
  line-height: 1.5;
}

.biomol-bottom {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 230px;
  gap: 14px;
}

.biomol-info-card,
.biomol-action-card {
  padding: 18px;
  border-radius: 20px;
  border: 1px solid #cddcec;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 30px rgba(7, 17, 31, 0.055);
}

.biomol-info-card p,
.biomol-action-card p {
  margin: 0 0 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.biomol-info-card h4 {
  margin: 0;
  color: #07111f;
  font-size: 18px;
  line-height: 1.18;
  font-weight: 950;
}

.biomol-info-card span {
  display: block;
  margin-top: 8px;
  color: #66778e;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.biomol-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.biomol-meta-grid div {
  padding: 12px;
  border-radius: 14px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
}

.biomol-meta-grid small {
  display: block;
  margin-bottom: 5px;
  color: #75869d;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.biomol-meta-grid b {
  color: #07111f;
  font-size: 13px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.biomol-action-card {
  display: grid;
  align-content: start;
  gap: 10px;
}

.biomol-action-card button {
  min-height: 42px;
  border: none;
  border-radius: 12px;
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
  cursor: pointer;
  font-size: 13px;
  font-weight: 950;
}

.biomol-action-card button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.biomol-action-card span {
  color: #66778e;
  font-size: 12px;
  line-height: 1.45;
}

@keyframes biomolOrbitPulse {
  0%, 100% {
    opacity: 0.20;
    transform: translate(-50%, -50%) scale(0.96);
  }

  50% {
    opacity: 0.55;
    transform: translate(-50%, -50%) scale(1.04);
  }
}

@media (max-width: 980px) {
  .biomol-bottom {
    grid-template-columns: 1fr;
  }

  .biomol-meta-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 760px) {
  .biomol-viewer {
    min-height: auto;
  }

  .biomol-head {
    display: grid;
  }

  .biomol-badge-stack {
    justify-content: flex-start;
  }

  .biomol-stage {
    min-height: 360px;
  }

  .biomol-svg {
    min-height: 360px;
  }

  .biomol-meta-grid {
    grid-template-columns: 1fr;
  }
}
`;

function getAtomColor(atom) {
  const symbol = String(atom?.element_symbol || atom?.symbol || "").toUpperCase();

  const map = {
    H: "#e5e7eb",
    C: "#38bdf8",
    N: "#60a5fa",
    O: "#ef4444",
    S: "#f59e0b",
    P: "#a78bfa",
    CL: "#22c55e",
    NA: "#f97316",
    K: "#fb7185",
    CA: "#fbbf24",
    MG: "#14b8a6",
    FE: "#94a3b8",
    ZN: "#06b6d4",
    CU: "#3b82f6",
    RX: "#22c55e",
    PR: "#a78bfa",
    CP: "#f97316",
  };

  return atom?.color || map[symbol] || "#18b7ff";
}

function shortText(value, limit = 42) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}...`;
}

function normalizeDetail(detail, selectedCompound) {
  const compound = detail?.compound || selectedCompound || null;

  const atoms = Array.isArray(detail?.atoms)
    ? detail.atoms
    : Array.isArray(compound?.atoms)
      ? compound.atoms
      : [];

  const bonds = Array.isArray(detail?.bonds)
    ? detail.bonds
    : Array.isArray(compound?.bonds)
      ? compound.bonds
      : [];

  return { compound, atoms, bonds };
}

function buildPositionedAtoms(atoms, width, height) {
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) * 0.30;

  return atoms.map((atom, index) => {
    const px = Number(atom.position_x);
    const py = Number(atom.position_y);

    if (Number.isFinite(px) && Number.isFinite(py) && (px !== 0 || py !== 0)) {
      return {
        ...atom,
        graph_x: centerX + px * 130,
        graph_y: centerY + py * 130,
      };
    }

    const angle =
      atoms.length <= 1 ? -Math.PI / 2 : (index / atoms.length) * Math.PI * 2 - Math.PI / 2;

    const ringJitter = atoms.length > 8 ? (index % 3) * 28 : 0;

    return {
      ...atom,
      graph_x: atoms.length === 1 ? centerX : centerX + Math.cos(angle) * (radius + ringJitter),
      graph_y: atoms.length === 1 ? centerY : centerY + Math.sin(angle) * (radius + ringJitter),
    };
  });
}

export default function BioMoleculeViewer3D({
  detail,
  selectedCompound,
  onSendToAvatar,
}) {
  const [selectedAtomLabel, setSelectedAtomLabel] = useState("");

  const { compound, atoms, bonds } = useMemo(
    () => normalizeDetail(detail, selectedCompound),
    [detail, selectedCompound]
  );

  const width = 920;
  const height = 460;

  const positionedAtoms = useMemo(
    () => buildPositionedAtoms(atoms, width, height),
    [atoms]
  );

  const atomByLabel = useMemo(() => {
    const map = new Map();

    positionedAtoms.forEach((atom, index) => {
      const label =
        atom.atom_label ||
        atom.label ||
        `${atom.element_symbol || atom.symbol || "X"}${index + 1}`;

      map.set(label, { ...atom, computed_label: label });
      map.set(String(atom.id), { ...atom, computed_label: label });
    });

    return map;
  }, [positionedAtoms]);

  const selectedAtom = useMemo(() => {
    return atomByLabel.get(selectedAtomLabel) || null;
  }, [atomByLabel, selectedAtomLabel]);

  const compoundName = compound?.name || selectedCompound?.name || "No compound selected";
  const formula =
    compound?.display_formula ||
    compound?.formula ||
    selectedCompound?.display_formula ||
    selectedCompound?.formula ||
    "Formula unavailable";

  const description =
    compound?.description ||
    selectedCompound?.description ||
    "Select a compound to inspect molecular atoms, bonds, and digital-avatar research context.";

  const canSend = Boolean(compound);

  return (
    <div className="biomol-viewer">
      <style>{moleculeViewerCss}</style>

      <div className="biomol-head">
        <div>
          <p>Molecule Preview</p>
          <h3>{compoundName}</h3>
          <span>{description}</span>
        </div>

        <div className="biomol-badge-stack">
          <em>{formula}</em>
          <em>{atoms.length} atoms</em>
          <em>{bonds.length} bonds</em>
        </div>
      </div>

      <div className="biomol-stage">
        <div className="biomol-orbit" />
        <div className="biomol-orbit o2" />
        <div className="biomol-orbit o3" />

        {!compound ? (
          <div className="biomol-empty">
            <div>
              <strong>No molecule selected</strong>
              <span>
                Pick a compound from the left list. The molecule preview and
                avatar queue actions will appear here.
              </span>
            </div>
          </div>
        ) : (
          <svg
            className="biomol-svg"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Bio molecule viewer"
          >
            <defs>
              <filter id="biomolGlow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              <linearGradient id="biomolBondGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#18b7ff" stopOpacity="0.28" />
                <stop offset="50%" stopColor="#a7f3d0" stopOpacity="0.82" />
                <stop offset="100%" stopColor="#18b7ff" stopOpacity="0.28" />
              </linearGradient>
            </defs>

            {bonds.map((bond, index) => {
              const fromKey =
                bond.from_atom_label ||
                bond.atom1_label ||
                bond.source ||
                bond.from_atom_id ||
                bond.atom1_id;

              const toKey =
                bond.to_atom_label ||
                bond.atom2_label ||
                bond.target ||
                bond.to_atom_id ||
                bond.atom2_id;

              const from = atomByLabel.get(String(fromKey));
              const to = atomByLabel.get(String(toKey));

              if (!from || !to) return null;

              const bondType = String(bond.bond_type || "single").toLowerCase();
              const thick =
                bondType === "triple" ? 8 : bondType === "double" ? 6 : 4;

              return (
                <g key={bond.id || `${fromKey}-${toKey}-${index}`} className="biomol-bond">
                  <line
                    x1={from.graph_x}
                    y1={from.graph_y}
                    x2={to.graph_x}
                    y2={to.graph_y}
                    stroke="url(#biomolBondGradient)"
                    strokeWidth={thick}
                    strokeLinecap="round"
                    opacity="0.86"
                  />
                  <text
                    x={(from.graph_x + to.graph_x) / 2}
                    y={(from.graph_y + to.graph_y) / 2 - 10}
                    textAnchor="middle"
                    fill="#c8f1ff"
                    fontSize="11"
                    fontWeight="850"
                    paintOrder="stroke"
                    stroke="#03060c"
                    strokeWidth="4"
                    strokeLinejoin="round"
                  >
                    {bondType}
                  </text>
                </g>
              );
            })}

            {positionedAtoms.map((atom, index) => {
              const label =
                atom.atom_label ||
                atom.label ||
                `${atom.element_symbol || atom.symbol || "X"}${index + 1}`;

              const symbol = atom.element_symbol || atom.symbol || "X";
              const selected = selectedAtomLabel === label;
              const color = getAtomColor(atom);

              return (
                <g
                  key={atom.id || label}
                  className={selected ? "biomol-node selected" : "biomol-node"}
                  transform={`translate(${atom.graph_x}, ${atom.graph_y})`}
                  onClick={() => setSelectedAtomLabel(label)}
                >
                  <circle
                    r={selected ? 30 : 25}
                    fill={color}
                    stroke="#ffffff"
                    strokeWidth="3"
                    filter="url(#biomolGlow)"
                  />
                  <circle
                    r="7"
                    cx="9"
                    cy="-10"
                    fill="#ffffff"
                    opacity="0.72"
                  />
                  <text
                    y="5"
                    textAnchor="middle"
                    fill="#ffffff"
                    fontSize="17"
                    fontWeight="950"
                  >
                    {symbol}
                  </text>
                  <text
                    y="45"
                    textAnchor="middle"
                    fill="#dbeafe"
                    fontSize="11"
                    fontWeight="850"
                    paintOrder="stroke"
                    stroke="#03060c"
                    strokeWidth="4"
                    strokeLinejoin="round"
                  >
                    {label}
                  </text>
                </g>
              );
            })}

            {compound && atoms.length === 0 && (
              <text
                x={width / 2}
                y={height / 2}
                textAnchor="middle"
                fill="#c8f1ff"
                fontSize="18"
                fontWeight="900"
              >
                Compound selected, but atom mapping is not available yet.
              </text>
            )}
          </svg>
        )}
      </div>

      <div className="biomol-bottom">
        <div className="biomol-info-card">
          <p>{selectedAtom ? "Selected Atom" : "Compound Details"}</p>

          {selectedAtom ? (
            <>
              <h4>
                {selectedAtom.element_name ||
                  selectedAtom.name ||
                  selectedAtom.element_symbol ||
                  "Atom"}
              </h4>
              <span>
                Label: {selectedAtom.computed_label || selectedAtom.atom_label || "—"} ·
                Symbol: {selectedAtom.element_symbol || selectedAtom.symbol || "—"} ·
                Atomic number: {selectedAtom.atomic_number || "—"}
              </span>
            </>
          ) : (
            <>
              <h4>{compoundName}</h4>
              <span>{description}</span>
            </>
          )}

          <div className="biomol-meta-grid">
            <div>
              <small>Formula</small>
              <b>{formula}</b>
            </div>

            <div>
              <small>Type</small>
              <b>{compound?.compound_type || selectedCompound?.compound_type || "compound"}</b>
            </div>

            <div>
              <small>Source</small>
              <b>{compound ? "Bio Lab" : "Waiting"}</b>
            </div>
          </div>
        </div>

        <div className="biomol-action-card">
          <p>Avatar Queue</p>
          <button
            type="button"
            disabled={!canSend}
            onClick={() => onSendToAvatar?.(compound, detail || { compound, atoms, bonds })}
          >
            Add to Avatar Queue
          </button>
          <span>
            Sends this molecule, atoms, and bonds into the research-only digital
            avatar simulation queue.
          </span>
        </div>
      </div>
    </div>
  );
}